import { useEffect, useMemo, useState } from "react";
import api from "../../api/client";

const SYSTEM_ORG_ID = 5;

const emptyOrg = {
  id: null,
  company_name: "",
  address: "",
  city: "",
  zip_code: "",
  country: "",
  company_email: "",
  is_active: true,
  org_role: "operator",
};

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyOrg);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const load = async () => {
    try {
      const resp = await api.get("/admin/organizations/");
      setOrgs(resp.data);
    } catch (err) {
      setError("Failed to load organizations");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyOrg);
    setModalOpen(true);
    setError("");
  };

  const openEdit = (org) => {
    setForm({
      id: org.id,
      company_name: org.company_name || "",
      address: org.address || "",
      city: org.city || "",
      zip_code: org.zip_code || "",
      country: org.country || "",
      company_email: org.company_email || "",
      is_active: Boolean(org.is_active),
      org_role: org.org_role || "operator",
    });
    setModalOpen(true);
    setError("");
  };

  const filteredOrgs = useMemo(() => {
    const term = search.toLowerCase();
    return orgs
      .filter((o) => {
      const matchesName = !term || (o.company_name || "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && o.is_active) ||
        (statusFilter === "inactive" && !o.is_active);
      return matchesName && matchesStatus;
      })
      .sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
  }, [orgs, search, statusFilter]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        company_name: form.company_name,
        address: form.address || null,
        city: form.city || null,
        zip_code: form.zip_code || null,
        country: form.country || null,
        company_email: form.company_email || null,
        is_active: form.is_active,
        org_role: form.org_role,
      };
      if (form.id) {
        await api.put(`/admin/organizations/${form.id}`, payload);
      } else {
        await api.post("/admin/organizations/", payload);
      }
      await load();
      setModalOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Organizations</h1>
          <p className="text-sm text-slate-500">Manage companies and their status.</p>
        </div>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800"
        >
          Add organization
        </button>
      </div>

      <div className="bg-white border rounded shadow-sm p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="flex-1">
          <label className="text-xs uppercase text-slate-500 font-semibold block">Search</label>
          <input
            className="mt-1 w-full rounded border px-3 py-2"
            placeholder="Search name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs uppercase text-slate-500 font-semibold block">Status</label>
          <select
            className="mt-1 rounded border px-3 py-2"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="bg-white border rounded shadow-sm divide-y">
        {filteredOrgs.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500">No organizations match the filters.</div>
        )}
        {filteredOrgs.map((org) => (
          <button
            key={org.id}
            onClick={() => openEdit(org)}
            className="w-full text-left px-4 py-4 hover:bg-slate-50 flex items-center justify-between"
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{org.company_name}</span>
                <span
                  className={`text-[11px] uppercase font-semibold px-2 py-0.5 rounded-full ${
                    org.id === SYSTEM_ORG_ID
                      ? "bg-slate-800 text-white"
                      : org.org_role === "owner"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {org.id === SYSTEM_ORG_ID
                    ? "Admin"
                    : org.org_role === "owner"
                    ? "Owner"
                    : "Operator"}
                </span>
              </div>
              <span className="text-xs text-slate-500">ID: {org.id}</span>
              {org.city && (
                <span className="text-xs text-slate-500">
                  {(org.address && `${org.address}, `) || ""}
                  {org.city} {org.zip_code || ""}
                  {org.country ? `, ${org.country}` : ""}
                </span>
              )}
            </div>
            <span
              className={`text-xs font-semibold px-2 py-1 rounded ${
                org.is_active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              {org.is_active ? "Active" : "Inactive"}
            </span>
          </button>
        ))}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-xl w-full max-w-2xl border">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {form.id ? "Edit organization" : "Add organization"}
              </h2>
              <button
                className="text-sm text-slate-500 hover:text-slate-800"
                onClick={() => setModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={onSave} className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-700">Name</label>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.company_name}
                    onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                    required
                  />
                </div>
            <div className="flex items-center gap-2 mt-1 md:mt-0">
              <input
                id="is_active"
                type="checkbox"
                className="h-4 w-4"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              <label htmlFor="is_active" className="text-sm text-slate-700">
                Active
              </label>
            </div>
            <div>
              <label className="text-sm text-slate-700">Org role</label>
              <select
                className="mt-1 w-full rounded border px-3 py-2"
                value={form.org_role}
                onChange={(e) => setForm((f) => ({ ...f, org_role: e.target.value }))}
              >
                <option value="owner">owner</option>
                <option value="operator">operator</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-700">Email</label>
              <input
                className="mt-1 w-full rounded border px-3 py-2"
                value={form.company_email}
                    onChange={(e) => setForm((f) => ({ ...f, company_email: e.target.value }))}
                    type="email"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Address</label>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-700">City</label>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Zip code</label>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.zip_code}
                    onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-700">Country</label>
                  <input
                    className="mt-1 w-full rounded border px-3 py-2"
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  />
                </div>
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded border text-slate-700 hover:bg-slate-50"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-60"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
